/**
 * 子パターン（#2967）で上書きできる項目の候補を、親パターンの item_list から組み立てる。
 *
 * 無効化された項目と計算フィールドは対象外。計算フィールドは式で値が決まるため、
 * AIへの指示文である取得ルールを書いても効かない。
 *
 * 一方、AI読取OFF（is_ocr_target=false）の項目は候補に含める。
 * 「親では手入力にしているが、この帳票にだけ印字されている」場合に
 * 子パターン側でONに切り替えられるようにするため（#2967）。
 */

import type { ItemListType } from '../../types/common';
import type { SubPatternOverrideCandidate } from '../../components/uiParts/modal/SubPatternEditModal';

/** その項目を上書きできるか（無効化された項目と計算フィールドを除く） */
const isOverridable = (item: { is_active?: boolean; is_calculated?: boolean }): boolean =>
  item.is_active !== false && item.is_calculated !== true;

/**
 * 上書き候補の一覧を作る。
 * 明細（array）の親項目自体は取得ルールを持つが値を持たないため候補に含め、
 * その列（子項目）も「親項目名 > 子項目名」として個別に上書きできるようにする。
 */
export const buildSubPatternOverrideCandidates = (
  itemList: ItemListType[],
): SubPatternOverrideCandidate[] => {
  const candidates: SubPatternOverrideCandidate[] = [];

  for (const item of itemList) {
    if (!isOverridable(item)) continue;

    candidates.push({
      itemId: item.id,
      itemName: item.parentValue,
      parentRule: item.extractionRule ?? '',
      parentFixedValue: item.fixedValue ?? '',
      itemType: item.type ?? 'string',
      parentIsOcrTarget: item.is_ocr_target !== false,
      // 明細（array）の親はAI読取のON/OFFを持たない。OFFにするとスキーマから
      // 明細ごと消えて結果の列が親と食い違うため、読み取りの可否は列ごとに設定する。
      // 読取パターンの項目編集モーダルでも同じ理由でトグルを出していない
      canToggleOcrTarget: item.type !== 'array',
      // 明細（array）の親には固定値を設定できない（行数が可変のため）
      canSetFixedValue: item.type !== 'array',
    });

    for (const child of item.children ?? []) {
      if (!isOverridable(child)) continue;
      candidates.push({
        itemId: child.id,
        itemName: `${item.parentValue} > ${child.childValue}`,
        parentRule: child.extractionRule ?? '',
        parentFixedValue: child.fixedValue ?? '',
        itemType: child.type ?? 'string',
        parentIsOcrTarget: child.is_ocr_target !== false,
        canToggleOcrTarget: true,
        canSetFixedValue: true,
      });
    }
  }

  return candidates;
};
